// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { ConfirmDialog } from '@/app/components/confirm-dialog';

interface UnsavedChangesGuardProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog requests to close. */
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms they want to discard changes. */
  onConfirm: () => void;
}

/**
 * Pre-configured confirmation dialog for unsaved form changes.
 *
 * Pair with the `useUnsavedChanges` hook:
 * ```tsx
 * const unsaved = useUnsavedChanges(formData);
 * // ...
 * <UnsavedChangesGuard {...unsaved.dialogProps} />
 * ```
 */
export function UnsavedChangesGuard({
  open,
  onOpenChange,
  onConfirm,
}: UnsavedChangesGuardProps) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Unsaved changes"
      description="You have unsaved changes that will be lost. Are you sure you want to discard them?"
      confirmLabel="Discard"
      cancelLabel="Keep editing"
      destructive
      onConfirm={onConfirm}
    />
  );
}
